import AdyenCheckout from '@adyen/adyen-web';
import { PaymentsStatus } from '@spike/payments-action';
import clsx from 'clsx';
import { ADYEN_RESULT_CODES } from 'components/AdyenDropIn/AdyenResultCodes';
import { Spinner } from 'components/UI/Spinner/Spinner';
import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

import '@adyen/adyen-web/dist/adyen.css';
import classes from './AdyenDropIn.module.scss';

export interface DropIn {
    amount: AmountDropIn;
    countryCode: string;
    channel: string;
    expiresAt: string;
    id: string;
    merchantAccount: string;
    reference: string;
    returnUrl: string;
    sessionData: string;
    shopperName: {
        firstName: string;
        lastName: string;
    };
    shopperReference: string;
    splits?: Array<SplitDropIn>;
    trustedShopper: boolean;
}

export interface SplitDropIn {
    account: string;
    amount: AmountDropIn;
    description: string;
    type: string;
}

export interface AmountDropIn {
    currency: string;
    value: number;
}

interface AdyenDropInProps {
    dropIn: DropIn;
    className?: string;
    onError?: (message: string) => void;
    onSuccess?: (sessionId: string) => void;
}

const NETWORK_ERROR = 'NETWORK_ERROR';

export const AdyenDropIn: React.FC<AdyenDropInProps> = props => {
    const dropInRef = React.useRef<HTMLInputElement>(null);

    const paymentsStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);

    const [isLoading, setIsLoading] = React.useState(true);

    React.useEffect(() => {
        if (props.dropIn) {
            const createCheckout = async () => {
                const configuration = {
                    environment: `${process.env.REACT_APP_ADYEN_ENVIRONMENT}`, // Change to 'live' for the live environment.
                    clientKey: `${process.env.REACT_APP_ADYEN_CLIENT_KEY}`, // Public key used for client-side authentication: https://docs.adyen.com/development-resources/client-side-authentication
                    session: {
                        id: props.dropIn!.id, // Unique identifier for the payment session.
                        sessionData: props.dropIn!.sessionData // The payment session data.
                    },
                    onPaymentCompleted: ({ resultCode }: { resultCode: string }) => {
                        const resultCodeInfo = ADYEN_RESULT_CODES.get(resultCode);

                        if (!resultCodeInfo || !resultCodeInfo.success) {
                            props.onError?.('An error as ocurred.');
                            return;
                        }

                        props.onSuccess?.(props.dropIn.id);
                    },
                    onError: (error: Error) => {
                        let errorMessage = 'An unexpected error has occured';

                        if (error.name === NETWORK_ERROR) {
                            errorMessage = 'Network error!';
                        } else {
                            errorMessage = 'An unexpected error has occured!';
                        }

                        props.onError?.(errorMessage);
                    },
                    // Any payment method specific configuration. Find the configuration specific to each payment method:  https://docs.adyen.com/payment-methods
                    paymentMethodsConfiguration: {
                        card: {
                            hasHolderName: true,
                            holderNameRequired: false,
                            billingAddressRequired: false
                        }
                    }
                };

                const checkout = await AdyenCheckout(configuration);

                if (dropInRef.current) {
                    checkout
                        .create('dropin', {
                            showStoredPaymentMethods: false
                        })
                        .mount(dropInRef.current);
                }
            };

            createCheckout();
            setIsLoading(false);
        } else if (PaymentsStatus.Error === paymentsStatus) {
            setIsLoading(false);
        }
        // eslint-disable-next-line
    }, []);

    return (
        <div className={clsx(classes.container, props.className)}>
            {isLoading && <Spinner />}

            <div id="dropin-container" ref={dropInRef} className={classes.dropIn}></div>
        </div>
    );
};
